import * as Sentry from '@sentry/react'
import PostHog from 'posthog-js'
import { ApolloError } from '@apollo/client'

import i18n from '@app/locales/i18n'

import { showToast } from './showToast'

const UNAUTHORIZED_MESSAGE = 'Request failed with status code 401'

export class LogUtils {
  /**
   * Log error to sentry
   * @param err Error received in exception
   * @param fileName Filename where exception was thrown
   * @param functionName Function where exception was thrown
   * @param errorText optional error text
   * @returns void
   */
  static logError = (
    err: any,
    fileName: string,
    functionName: string,
    errorText: string = '',
    shouldShowToast = true
  ): void => {
    if (shouldShowToast) {
      let traceId = undefined
      // Access request headers from Apollo error
      if (err instanceof Error && 'networkError' in err) {
        const apolloError = err as any
        if (apolloError.graphQLErrors?.[0]?.extensions?.traceId) {
          traceId = apolloError.graphQLErrors[0].extensions.traceId
        }
      }

      showToast(errorText ? errorText : i18n.t('errorGlobal', { ns: 'common' }), { type: 'error', data: { traceId } })
    }

    if (import.meta.env.DEV === true) {
      console.error(`[${new Date().toISOString()}]: ${fileName}:${functionName}:${errorText}`, err)
    } else {
      if (err instanceof Error || err instanceof ApolloError) {
        // Do not log unauthorized
        if (err.message.includes(UNAUTHORIZED_MESSAGE)) return
      }

      Sentry.captureException(err, {
        data: { fileName, functionName, errorText, timestamp: new Date().toISOString() }
      })
    }
  }

  /**
   *
   * @param err Get message from error
   * @returns Message from error as string
   */
  static getMessageFromError = (err: any): string => {
    if (err instanceof Error || err instanceof ApolloError) {
      return err.message
    } else if (typeof err === 'string') {
      return err
    }

    return i18n.t('errorGlobal')
  }

  /**
   * Log event to google analytics
   * @param name Event name
   * @param params Key value pairs to log
   */
  static logEvent = (
    category: LogCategory,
    name: string,
    eventType: LogEventType,
    params?: { [key: string]: string }
  ) => {
    if (import.meta.env.DEV) {
      console.info(`[LOG-EVENT:${new Date().toISOString()}]: ${category}:${name}:${eventType}`, params)
    }

    try {
      PostHog.capture(name, {
        category,
        properties: {
          ...params,
          eventType
        }
      })
    } catch (error) {
      this.logError(error, 'LogUtils', 'logEvent')
    }
  }

  /**
   * Log screen name to google analytics
   * @param name Name of the screen
   */
  static logScreenView = (page: string) => {
    try {
      PostHog.capture('$pageview', { page })
    } catch (error) {
      this.logError(error, 'LogUtils', 'logEvent')
    }
  }
}

export type LogCategory = 'activity' | 'dictionary' | 'game' | 'learningPlan' | 'library' | 'performance' | 'settings'

export type LogEventType =
  | 'click_event'
  | 'view_event'
  | 'interaction_event'
  | 'navigation_event'
  | 'error_event'
  | 'custom_event'
  | 'drag_drop_event'
